import { Activity, CalendarDays, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { StatStrip, StatStripSkeleton } from "@/components/layout/StatStrip";
import { useLanguage } from "@/i18n";
import { cn } from "@/lib/utils";
import { slateCardClass } from "@/lib/card-styles";
import { typeBodyClass, typeTitleClass } from "@/lib/typography";

interface UserStats {
  totalSessions: number;
  publicSessions: number;
  totalGroups: number;
  totalTimeSeconds: number;
  topicCount: number;
  accountCreated: string | null;
  lastActivity: string | null;
}

interface StatisticsSectionProps {
  stats: UserStats | undefined;
  isLoading: boolean;
  currentStreak?: number;
  longestStreak?: number;
}

/** "3h 42m" — hours only once there are any, so a short session reads "18m". */
function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return "0m";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/**
 * How much you have done — total time first, then quiet supporting counts.
 *
 * ## This was an eight-card wall
 *
 * Eight bordered cards, each with its own coloured icon square (blue, primary,
 * orange, amber, purple), a `bg-gradient-to-br` hero card for the headline
 * figure, and `hover:shadow-md` on all of them. Every cell looked equally
 * urgent, so nothing did — and the "Member since" date was rendered in
 * `text-lg` against `text-2xl` counts that are actual statistics, which is the
 * wrong way round.
 *
 * `StatStrip` is the system's answer to exactly this: one primary figure, then
 * hairline-separated supporting counts. Time spent is the honest headline for
 * "your activity", and the two streak figures move to the footer rather than
 * competing for a cell — they are also on the modal header, one click away.
 */
export const StatisticsSection = ({
  stats,
  isLoading,
  currentStreak = 0,
  longestStreak = 0,
}: StatisticsSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className={typeTitleClass}>{t("settings.profile.statistics", "Statistics")}</h3>
          <p className={cn(typeBodyClass, "mt-1")}>
            {t("pages.settings.statisticsSubtitle", "Your activity on the platform")}
          </p>
        </div>
        <StatStripSkeleton />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className={typeTitleClass}>{t("settings.profile.statistics", "Statistics")}</h3>
          <p className={cn(typeBodyClass, "mt-1")}>
            {t("pages.settings.statisticsSubtitle", "Your activity on the platform")}
          </p>
        </div>
        <div
          className={cn(
            slateCardClass,
            "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
          )}
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-stone-100 text-stone-400 dark:bg-stone-800 dark:text-stone-500">
            <Activity className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="text-sm font-semibold text-stone-900 dark:text-stone-50">
            {t("pages.settings.noStatistics", "No statistics yet")}
          </p>
          <p className={cn(typeBodyClass, "max-w-xs")}>
            {t("pages.settings.noStatisticsHint", "Start practising to see your progress")}
          </p>
        </div>
      </div>
    );
  }

  // Dates must be formatted against the app's language, not the browser's — a
  // bare `toLocaleDateString()` here ignores a reader who has explicitly
  // switched the interface to Vietnamese.
  const formatDate = (value: string | null) =>
    value ? language?.format.date(value, { dateStyle: "medium" }) ?? "" : "";

  return (
    <div className="space-y-6">
      <div>
        <h3 className={typeTitleClass}>{t("settings.profile.statistics", "Statistics")}</h3>
        <p className={cn(typeBodyClass, "mt-1")}>
          {t("pages.settings.statisticsSubtitle", "Your activity on the platform")}
        </p>
      </div>

      <StatStrip
        primary={{
          label: t("pages.settings.totalLearningTime", "Learning time"),
          value: formatDuration(stats.totalTimeSeconds),
          icon: Clock,
          hint: t("pages.settings.topicsStudied", {
            defaultValue: "{{count}} topics studied",
            count: stats.topicCount,
          }),
        }}
        secondary={[
          // Short labels, no hints. `StatStrip` gives each cell `flex-1`, so at
          // this pane's width "SESSIONS" plus a "Practised" hint truncated to
          // "SES…" / "Pract…" — three unreadable columns. The hints restated
          // the label anyway ("Public / Shared", "Groups / Joined"), so
          // dropping them costs no information and the labels now fit.
          { label: t("pages.settings.totalSessions", "Sessions"), value: stats.totalSessions },
          { label: t("pages.settings.publicSessions", "Public"), value: stats.publicSessions },
          { label: t("pages.settings.studyGroupsShort", "Groups"), value: stats.totalGroups },
        ]}
      />

      {/* Membership and last seen are dates, not counts — a different kind of
          fact, so they get a list rather than cells in the strip above. */}
      <div className={cn(slateCardClass, "divide-y divide-stone-200/70 dark:divide-stone-800")}>
        {stats.accountCreated && (
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="flex-1 text-sm font-medium text-stone-700 dark:text-stone-300">
              {t("pages.settings.memberSince", "Member since")}
            </span>
            <span className="text-sm text-stone-500 dark:text-stone-400">
              {formatDate(stats.accountCreated)}
            </span>
          </div>
        )}
        {stats.lastActivity && (
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400">
              <Activity className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="flex-1 text-sm font-medium text-stone-700 dark:text-stone-300">
              {t("pages.settings.lastActivity", "Last activity")}
            </span>
            <span className="text-sm text-stone-500 dark:text-stone-400">
              {formatDate(stats.lastActivity)}
            </span>
          </div>
        )}
      </div>

      {/* Streak lives here rather than in the strip: it is already on the modal
          header one row above, and the header's copy is the one that invites
          the click through to the streak detail. */}
      <div className={cn(slateCardClass, "flex items-center justify-between gap-4 px-4 py-3")}>
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
          {t("pages.settings.streakRecord", "Streak record")}
        </span>
        <span className="flex items-center gap-4 text-sm text-stone-500 dark:text-stone-400">
          <span>
            {t("pages.settings.currentShort", "Now")}{" "}
            <span className="font-semibold tabular-nums text-stone-900 dark:text-stone-50">
              {currentStreak}
            </span>
          </span>
          <span className="text-stone-300 dark:text-stone-600" aria-hidden="true">
            /
          </span>
          <span>
            {t("pages.settings.bestShort", "Best")}{" "}
            <span className="font-semibold tabular-nums text-stone-900 dark:text-stone-50">
              {longestStreak}
            </span>
          </span>
        </span>
      </div>
    </div>
  );
};

export default StatisticsSection;